import React from 'react'

/**
 * Wraps React.lazy with automatic page reload on chunk load failures.
 *
 * After a deployment the browser may hold a stale index.html that references
 * chunk filenames from the previous build. When the server can't find the old
 * chunk it returns the SPA fallback (text/html) instead of JS, which causes a
 * TypeError. This wrapper catches that error and reloads the page once so the
 * browser fetches the latest index.html.
 *
 * A sessionStorage flag prevents infinite reload loops if the new build also
 * fails for an unrelated reason.
 */
const RELOAD_FLAG = 'chunk_reload'

export function importWithRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch((error: unknown) => {
    let hasReloaded = false
    try {
      hasReloaded = !!sessionStorage.getItem(RELOAD_FLAG)
    } catch {
      // sessionStorage unavailable — skip retry logic
      throw error
    }

    if (!hasReloaded) {
      try {
        sessionStorage.setItem(RELOAD_FLAG, '1')
      } catch {
        // Cannot persist flag — skip reload to avoid infinite loop risk
        throw error
      }
      window.location.reload()
      // Return a promise that never resolves — the reload will take over
      return new Promise<never>(() => {})
    }

    // Already reloaded once — clear the flag and let the error propagate
    try {
      sessionStorage.removeItem(RELOAD_FLAG)
    } catch {
      // Ignore — flag clearance is non-critical
    }
    throw error
  })
}

export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithRetry(importFn))
}

/**
 * Call this on successful app hydration to clear the reload flag so that
 * future deploys can trigger a fresh reload.
 */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // sessionStorage unavailable — non-critical
  }
}
